import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FEATURE_THRESHOLD } from '@mv/contracts';
import { IconBack, IconCheck, IconClose, IconPencil, IconPlay, IconTrash } from '../components/Icon';
import LikeButton from '../components/LikeButton';
import { remove, setCaption, setFeatured, useLiked, useMedia, visibleTo } from '../lib/media';
import { inTab, isTab } from '../lib/gallery-tabs';
import { useSession } from '../lib/session';
import { useSchedule } from '../lib/content';
import { relativeUploadTime } from '../lib/time';
import NotFound from './NotFound';

/**
 * Artboard 1j — the only place a video plays (D26), and the only place an
 * organiser curates by hand (D50).
 *
 * It is dark in both themes on purpose: the photograph is the light source, and
 * a cream surround under a reception photo washes it out.
 *
 * **The photograph takes the whole screen and every control floats over it**,
 * in translucent circles at the corners: back and edit top-left, the wall
 * toggle and delete top-right, the heart bottom-right, attribution and caption
 * along the bottom. The chrome used to be a bar above and up to four stacked
 * rows below, which on a phone left the photograph a letterbox in the middle of
 * its own screen.
 *
 * **Delete moved deliberately.** It was a full-width button at the bottom,
 * directly under the thumb and next to the controls a guest uses constantly —
 * the easiest thing on the screen to hit by accident, for the one action that
 * cannot be undone. It is now a small circle in the far corner, and it still
 * asks.
 *
 * Every overlay here is `position: absolute`, and that is load-bearing rather
 * than stylistic: `.viewer-stage` is a one-cell grid, and an in-flow child
 * lands in an implicit `auto` row that re-breaks the portrait-crop bug the
 * comment above it describes at length.
 */
export default function PhotoDetail() {
  const { photoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const tab = params.get('tab');
  const liked = useLiked();

  /**
   * Back goes back, rather than to a hardcoded `/gallery`.
   *
   * The hardcoded version threw away both the tab a guest was on and the
   * surface they came from — tapping a photo in the "Coming in" strip on Now
   * and pressing back dropped them into the gallery. `key === 'default'` means
   * this is the first entry in the session, which is what a direct link or a
   * refresh on this URL looks like; there is nothing to go back to, so the
   * gallery is the right landing.
   */
  const goBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/gallery');
  };
  const session = useSession();
  const media = useMedia();
  const schedule = useSchedule();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [playing, setPlaying] = useState(false);
  const touchStart = useRef<number | null>(null);

  /**
   * Next and previous walk **the tab you came from**, not the whole week.
   *
   * The gallery puts `?tab=` on its tiles; the ambient strips do not, because
   * they are the firehose and belong to no tab. Without this, opening the
   * twelfth featured photo and swiping wandered into the other two hundred and
   * thirty-eight, under a header reading "1 of 250".
   *
   * The predicates come from `gallery-tabs` rather than being restated here —
   * a copy that drifted from the grid's would step onto photographs the tab
   * does not contain, which is the bug this fixes wearing a subtler face.
   */
  const everything = useMemo(() => visibleTo(media, session), [media, session]);
  const items = useMemo(() => {
    if (!isTab(tab)) return everything;
    const inside = inTab(everything, tab, session, liked);
    // A photo can fall out of its own tab while you are looking at it — unlike
    // it from Liked, or an organiser takes it off the wall while you are in
    // Featured. Falling back to the full list keeps it on screen rather than
    // answering `NotFound` for a photograph that plainly exists.
    return inside.some((it) => it.id === photoId) ? inside : everything;
  }, [everything, tab, session, liked, photoId]);
  const index = items.findIndex((it) => it.id === photoId);
  const item = index >= 0 ? items[index] : undefined;

  /**
   * Stepping to the next photo keeps `?tab=`.
   *
   * Without it the very first step drops the tab and the viewer silently
   * reverts to walking every photograph of the week — the counter going
   * `1/12` then `2/250` is the whole symptom, and it looks like the scoping
   * never worked rather than like it worked once.
   */
  const search = params.toString();
  const go = useMemo(() => (delta: number) => {
    const next = items[index + delta];
    if (next) {
      navigate(`/gallery/${next.id}${search ? `?${search}` : ''}`, { replace: true });
    }
  }, [items, index, navigate, search]);

  useEffect(() => {
    setEditing(false);
    setConfirmingDelete(false);
    setPlaying(false);
  }, [photoId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'Escape') { if (confirmingDelete) setConfirmingDelete(false); else goBack(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- goBack is stable
    // enough: it closes over `navigate` and `location.key`, and re-binding the
    // listener on every location change would be churn for no behaviour change.
  }, [go, navigate, location.key, confirmingDelete]);

  if (!item) return <NotFound />;

  const mine = item.uploaderId === session?.userId;
  /* The threshold is a shared constant rather than a number the client guessed,
     precisely so this sentence cannot disagree with the server that enforces
     it — a client shipping a stale copy would tell a guest a photo is one like
     from the wall when it is two.
     `featureLocked` is the other half, and it is the half that bites: an
     organiser who takes a photo off the wall leaves a lock, likes keep
     accumulating underneath it, and a photo sitting at one short would
     otherwise be promised a promotion that can never arrive. */
  const oneShort = !item.featured && !item.featureLocked
    && item.likeCount === FEATURE_THRESHOLD - 1;
  const eventName = schedule.data?.events.find((e) => e.id === item.eventId)?.name;

  function saveCaption() {
    setCaption(item!.id, draft);
    setEditing(false);
  }

  return (
    <div
      className="viewer"
      onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
        touchStart.current = null;
      }}
    >
      <div className="viewer-stage">
        {item.kind === 'video' ? (
          playing ? (
            <video src={item.src} controls autoPlay playsInline />
          ) : (
            <button
              type="button"
              className="viewer-play"
              onClick={() => setPlaying(true)}
              aria-label="Play video"
            >
              <img src={item.thumb} alt="" />
              <span className="tile-play"><span><IconPlay size={20} /></span></span>
            </button>
          )
        ) : (
          <img src={item.src} alt={item.caption ?? `Photo from ${item.uploaderName}`} />
        )}

        {index > 0 && <button type="button" className="viewer-nav prev" onClick={() => go(-1)}>Previous</button>}
        {index < items.length - 1 && <button type="button" className="viewer-nav next" onClick={() => go(1)}>Next</button>}

        <div className="vw vw-tl">
          <button type="button" className="vw-btn" onClick={goBack} aria-label="Back to the gallery">
            <IconBack size={19} />
          </button>
          {mine && !editing && (
            <button
              type="button" className="vw-btn"
              onClick={() => { setDraft(item.caption ?? ''); setEditing(true); }}
              aria-label="Edit caption"
            >
              <IconPencil size={18} />
            </button>
          )}
        </div>

        <div className="vw vw-tr">
          {mine && (
            <button
              type="button" className="vw-btn is-danger"
              onClick={() => setConfirmingDelete(true)}
              aria-label="Delete this photo"
            >
              <IconTrash size={18} />
            </button>
          )}
        </div>

        <div className="vw vw-br">
          {session?.isAdmin && (
            /* A word, not a second heart. Featuring and liking are different
               acts and two hearts side by side said nothing about which was
               which — the organiser's control is rarer and can afford the
               space a label costs.

               Reversible, no confirmation, state visible in the fill.
               Organisers cannot delete: that stays with the owner (D44).
               Taking a photo off the wall also locks it, so three further likes
               cannot undo the decision (D50). */
            <button
              type="button" className={`vw-pill${item.featured ? ' is-on' : ''}`}
              onClick={() => setFeatured(item.id, !item.featured)}
              aria-pressed={item.featured}
              /* The accessible name leads with the visible word. A control
                 labelled "Unfeature" whose accessible name is only "take off
                 the big screen" cannot be reached by anyone driving the page by
                 voice, who says what they can see (WCAG 2.5.3). */
              aria-label={item.featured
                ? 'Unfeature — take off the big screen'
                : 'Feature — put on the big screen'}
            >
              {item.featured ? 'Unfeature' : 'Feature'}
            </button>
          )}
          <LikeButton item={item} size="lg" />
        </div>

        <div className="vw vw-bl">
          <div className="vw-who">
            {item.uploaderName}
            <span className="vw-sep">·</span>{relativeUploadTime(item.createdAt)}
            {eventName && <><span className="vw-sep">·</span>{eventName}</>}
            <span className="vw-sep">·</span>
            <span className="tag-count">{index + 1}/{items.length}</span>
            {item.featured && <span className="vw-sep">·</span>}
            {item.featured && <span className="vw-wall">On the wall</span>}
          </div>
          {/* No placeholder when there is no caption. "Add a caption" sat there
              looking like a control and was not one — the pencil above is the
              affordance, and an empty line is quieter than a false one. */}
          {!editing && item.caption && <p className="vw-caption">{item.caption}</p>}
          {oneShort && <p className="vw-hint">One more like puts this on the big screen.</p>}
          {item.featured && item.featuredBy === 'likes' && !oneShort && (
            <p className="vw-hint">Liked onto the big screen.</p>
          )}
        </div>

        {editing && (
          <div className="vw-edit">
            <input
              className="input" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCaption(); }}
              placeholder="Say what was happening"
            />
            <button type="button" className="vw-btn" onClick={saveCaption} aria-label="Save caption">
              <IconCheck size={15} />
            </button>
            <button type="button" className="vw-btn" onClick={() => setEditing(false)} aria-label="Cancel">
              <IconClose size={15} />
            </button>
          </div>
        )}

        {confirmingDelete && (
          /* Still asks, even from a corner button. Delete is the one thing here
             that cannot be undone, and D44 makes the owner the only person who
             can do it — so there is nobody to put it back. */
          <div className="vw-confirm" role="dialog" aria-label="Delete this photo">
            <p>Delete this photo? It cannot be undone.</p>
            <div className="vw-confirm-row">
              <button
                type="button" className="btn vw-danger"
                onClick={() => { remove(item.id); goBack(); }}
              >
                Delete
              </button>
              <button type="button" className="btn" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
